import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Paper,
  useTheme
} from '@mui/material';
import {
  WallpaperOutlined,
  FileUploadOutlined,
  DeleteOutline,
  AddPhotoAlternate
} from '@mui/icons-material';

import WallpaperGallery from '../WallpaperGallery';
import { SCALING_OPTIONS } from '../../constants/wallpaperConstants';

const DesignSettings = ({ initialSettings, onSave }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [settings, setSettings] = useState({
    wallpaper: null,
    customWallpaper: null,
    wallpaperScaling: 'cover'
  });
  const [customWallpaperPreview, setCustomWallpaperPreview] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);

  // Initialize settings from props
  useEffect(() => {
    if (initialSettings) {
      setSettings(prev => ({
        ...prev,
        wallpaper: initialSettings.wallpaper || null,
        customWallpaper: initialSettings.customWallpaper || null,
        wallpaperScaling: initialSettings.wallpaperScaling || 'cover'
      }));

      // Set custom wallpaper preview if exists
      if (initialSettings.customWallpaper) {
        setCustomWallpaperPreview(initialSettings.customWallpaper);
      }
    }
  }, [initialSettings]);

  const handleWallpaperChange = (wallpaperId) => {
    // If selecting a predefined wallpaper, clear custom wallpaper
    const updatedSettings = {
      ...settings,
      wallpaper: wallpaperId === 'default' ? null : wallpaperId,
      customWallpaper: null
    };
    
    setSettings(updatedSettings);
    setCustomWallpaperPreview(null);
    onSave(updatedSettings);
  };

  const handleCustomWallpaperUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.match('image.*')) {
      alert(t('settings.design.invalidImageType'));
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('settings.design.imageTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageDataUrl = e.target.result;
      
      // Update settings with custom wallpaper
      const updatedSettings = {
        ...settings,
        wallpaper: 'custom',
        customWallpaper: imageDataUrl
      };
      
      setSettings(updatedSettings);
      setCustomWallpaperPreview(imageDataUrl);
      onSave(updatedSettings);
    };
    reader.readAsDataURL(file);
  };

  const handleClearWallpaper = () => {
    const updatedSettings = {
      ...settings,
      wallpaper: null,
      customWallpaper: null
    };
    
    setSettings(updatedSettings);
    setCustomWallpaperPreview(null);
    onSave(updatedSettings);
  };

  const handleScalingChange = (event) => {
    const scalingOption = event.target.value;
    const updatedSettings = {
      ...settings,
      wallpaperScaling: scalingOption
    };
    
    setSettings(updatedSettings);
    onSave(updatedSettings);
  };

  const openWallpaperGallery = () => {
    setGalleryOpen(true);
  };

  const closeWallpaperGallery = () => {
    setGalleryOpen(false);
  };

  const selectedWallpaper = settings.wallpaper === 'custom' 
    ? 'custom'
    : settings.wallpaper || 'default';

  const wallpaperSelected = settings.wallpaper !== null || customWallpaperPreview !== null;

  // Helper function to get file format from ID
  const getFileFormat = (wallpaperId) => {
    if (!wallpaperId || wallpaperId === 'default' || wallpaperId === 'custom') return '';
    
    const extension = wallpaperId.substring(wallpaperId.lastIndexOf('.') + 1).toUpperCase();
    return extension;
  };

  return (
    <List>
      {/* Chat Wallpaper Settings */}
      <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WallpaperOutlined sx={{ mr: 1 }} />
              {t('settings.design.chatWallpaper')}
            </Box>
          }
          secondary={t('settings.design.chatWallpaperDescription')}
          sx={{ mb: 2 }}
        />

        {/* Wallpaper Selection Button */}
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddPhotoAlternate />}
            onClick={openWallpaperGallery}
          >
            {t('settings.design.selectWallpaper', { defaultValue: 'Select Wallpaper' })}
          </Button>

          <Button
            variant="outlined"
            component="label"
            startIcon={<FileUploadOutlined />}
          >
            {t('settings.design.uploadImage')}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleCustomWallpaperUpload}
            />
          </Button>
          
          {wallpaperSelected && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={handleClearWallpaper}
            >
              {t('settings.design.removeWallpaper')}
            </Button>
          )}
        </Box>

        {/* Wallpaper Scaling Options */}
        {wallpaperSelected && (
          <Box sx={{ mb: 3, width: '100%', maxWidth: 400 }}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="wallpaper-scaling-label">
                {t('settings.design.imageScaling', { defaultValue: 'Image Scaling' })}
              </InputLabel>
              <Select
                labelId="wallpaper-scaling-label"
                value={settings.wallpaperScaling}
                onChange={handleScalingChange}
                label={t('settings.design.imageScaling', { defaultValue: 'Image Scaling' })}
              >
                {SCALING_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(`settings.design.scaling.${option.value}`, { defaultValue: option.label })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Preview of current wallpaper */}
            {customWallpaperPreview ? (
              <Paper
                elevation={3}
                sx={{
                  position: 'relative',
                  height: 200,
                  width: '100%',
                  backgroundImage: `url(${customWallpaperPreview})`,
                  backgroundSize: settings.wallpaperScaling === 'cover' ? 'cover' : 
                                 settings.wallpaperScaling === 'contain' ? 'contain' : 
                                 settings.wallpaperScaling === 'tile' ? 'auto' : 'auto',
                  backgroundPosition: 'center',
                  backgroundRepeat: settings.wallpaperScaling === 'tile' ? 'repeat' : 'no-repeat',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                }}
              >
                <Box 
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    left: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    padding: '4px 8px',
                  }}
                >
                  <Typography variant="caption">
                    {t('settings.design.previewWithScaling', { 
                      defaultValue: 'Preview with {{scaling}} scaling',
                      scaling: t(`settings.design.scaling.${settings.wallpaperScaling}`, { 
                        defaultValue: SCALING_OPTIONS.find(o => o.value === settings.wallpaperScaling)?.label 
                      })
                    })}
                    {settings.wallpaper && settings.wallpaper !== 'custom' && settings.wallpaper !== 'default' && (
                      <span> [{getFileFormat(settings.wallpaper)}]</span>
                    )}
                    {settings.wallpaper === 'custom' && (
                      <span> [CUSTOM]</span>
                    )}
                  </Typography>
                </Box>
              </Paper>
            ) : (
              settings.wallpaper && settings.wallpaper !== 'default' && (
                <Paper
                  elevation={3}
                  sx={{
                    position: 'relative',
                    height: 200,
                    width: '100%',
                    backgroundImage: `url(/wallpapers/${settings.wallpaper})`,
                    backgroundSize: settings.wallpaperScaling === 'cover' ? 'cover' : 
                                   settings.wallpaperScaling === 'contain' ? 'contain' : 
                                   settings.wallpaperScaling === 'tile' ? 'auto' : 'auto',
                    backgroundPosition: 'center',
                    backgroundRepeat: settings.wallpaperScaling === 'tile' ? 'repeat' : 'no-repeat',
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                  }}
                >
                  <Box 
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      left: 0,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      padding: '4px 8px',
                    }}
                  >
                    <Typography variant="caption">
                      {t('settings.design.previewWithScaling', { 
                        defaultValue: 'Preview with {{scaling}} scaling',
                        scaling: t(`settings.design.scaling.${settings.wallpaperScaling}`, { 
                          defaultValue: SCALING_OPTIONS.find(o => o.value === settings.wallpaperScaling)?.label 
                        })
                      })}
                      {settings.wallpaper && settings.wallpaper !== 'custom' && settings.wallpaper !== 'default' && (
                        <span> [{getFileFormat(settings.wallpaper)}]</span>
                      )}
                    </Typography>
                  </Box>
                </Paper>
              )
            )}
          </Box>
        )}
      </ListItem>

      {/* Wallpaper Gallery Dialog */}
      <WallpaperGallery
        open={galleryOpen}
        onClose={closeWallpaperGallery}
        onSelect={handleWallpaperChange}
        selectedWallpaper={selectedWallpaper}
      />
    </List>
  );
};

export default DesignSettings;